/**
 * ActivityListItem - Activity variant design
 *
 * Renders icon-based list items with badges and points.
 * Used internally by CList when variant="activity".
 */
"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { CBadge } from "../CBadge/CBadge";
import {
  Star,
  Trophy,
  GraduationCap,
  TrendingUp,
  Heart,
  Calendar,
  Users,
  Zap,
  CheckCircle,
} from "lucide-react";
import type { ActivityItemType, ActivityBadgeVariant } from "./types";

interface ActivityListItemProps {
  items: ActivityItemType[];
  showDivider?: boolean;
}

/** Maps badge variant to points text color */
const getPointsColorClass = (variant: ActivityBadgeVariant): string => {
  const colorMap: Record<ActivityBadgeVariant, string> = {
    amber: "text-amber-600 dark:text-amber-400",
    indigo: "text-indigo-600 dark:text-indigo-400",
    "indigo-solid": "text-indigo-600 dark:text-indigo-400",
    blue: "text-blue-600 dark:text-blue-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    pink: "text-pink-600 dark:text-pink-400",
    violet: "text-violet-600 dark:text-violet-400",
    teal: "text-teal-600 dark:text-teal-400",
    red: "text-red-500 dark:text-red-400",
  };
  return colorMap[variant] || colorMap.emerald;
};

/** Icon key to component mapping */
const iconMap: Record<string, React.ReactNode> = {
  star: <Star className="w-4 h-4" />,
  trophy: <Trophy className="w-4 h-4" />,
  graduation: <GraduationCap className="w-4 h-4" />,
  trending: <TrendingUp className="w-4 h-4" />,
  heart: <Heart className="w-4 h-4" />,
  calendar: <Calendar className="w-4 h-4" />,
  users: <Users className="w-4 h-4" />,
  zap: <Zap className="w-4 h-4" />,
  check: <CheckCircle className="w-4 h-4" />,
};

const ActivityListItem: React.FC<ActivityListItemProps> = ({
  items,
  showDivider = true,
}) => {
  return (
    <div className="flex flex-col">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const resolvedIcon =
          item.icon ?? (item.iconKey ? iconMap[item.iconKey] : null);

        return (
          <div
            key={item.id}
            className={cn(
              "flex items-center py-3",
              showDivider &&
                !isLast &&
                "border-b border-slate-200 dark:border-gray-700",
            )}
          >
            {/* Icon */}
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center mr-3 shrink-0",
                item.iconBgColor,
              )}
            >
              <div className={cn("w-4 h-4", item.iconColor)}>
                {resolvedIcon}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "text-[13px] font-medium truncate",
                    "text-slate-900 dark:text-gray-100",
                  )}
                >
                  {item.title}
                </span>
                <CBadge
                  variant={item.badge.variant}
                  size="xs"
                  className="shrink-0"
                >
                  {item.badge.text}
                </CBadge>
              </div>
              {item.subtitle && (
                <div
                  className={cn(
                    "text-[11px]",
                    "text-slate-400 dark:text-gray-500",
                  )}
                >
                  {item.subtitle}
                </div>
              )}
            </div>

            {/* Points */}
            {item.points !== undefined && item.points !== 0 && (
              <div className="flex items-center gap-3 ml-3">
                <span
                  className={cn(
                    "text-xs font-semibold",
                    item.pointsColor || getPointsColorClass(item.badge.variant),
                  )}
                >
                  +{item.points}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {items.length === 0 && (
        <div
          className={cn(
            "py-8 text-center text-sm",
            "text-slate-400 dark:text-gray-500",
          )}
        >
          No activities to display
        </div>
      )}
    </div>
  );
};

export default ActivityListItem;
